import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { faArrowRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LocalStorage } from '../utils/LocalStorage';
import { ChannelLogo } from './channels/ChannelLogo';
import {
    GOOGLE_CHANNEL, FACEBOOK_CHANNEL, TIKTOK_CHANNEL, LINKEDIN_CHANNEL,
    ANALYTICS_CHANNEL, SEARCH_CONSOLE_CHANNEL, DV360_CHANNEL, ESCALAFY_CHANNEL, MERCADO_ADS_CHANNEL,
    CHANNEL_INFO_BY_CHANNEL,
} from './channels/channels';
import './DisconnectedCredentialsBanner.scss';

const REFRESH_INTERVAL_MS = 5000;
const CONNECTIONS_PATH = '/agency/conections';
const AUTH_PATHS = ['/login', '/signup'];

// Fingerprint determinístico del set de canales desconectados.
// Si el set cambia, el dismiss anterior queda inválido y el banner reaparece.
function fingerprintOf(channels) {
    return [...channels].sort().join(',');
}

const CHANNEL_DISPLAY_NAMES = {
    [GOOGLE_CHANNEL]: 'Google Ads',
    [FACEBOOK_CHANNEL]: 'Meta Ads',
    [TIKTOK_CHANNEL]: 'TikTok Ads',
    [LINKEDIN_CHANNEL]: 'LinkedIn Ads',
    [ANALYTICS_CHANNEL]: 'GA4',
    [SEARCH_CONSOLE_CHANNEL]: 'Search Console',
    [DV360_CHANNEL]: 'Display & Video 360',
    [MERCADO_ADS_CHANNEL]: 'Mercado Ads',
    [ESCALAFY_CHANNEL]: 'Escalafy',
};

const MAX_LOGOS_INLINE = 4;

export const DisconnectedCredentialsBanner = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [active, setActive] = useState(false);
    const [channels, setChannels] = useState([]);
    const [dismissedFp, setDismissedFp] = useState(
        LocalStorage.fetchDismissedDisconnectedFingerprint()
    );

    useEffect(() => {
        const readFromStorage = () => {
            setActive(LocalStorage.fetchHasDisconnectedCredentials());
            setChannels(LocalStorage.fetchDisconnectedCredentialsChannels());
            setDismissedFp(LocalStorage.fetchDismissedDisconnectedFingerprint());
        };

        readFromStorage();
        const interval = setInterval(readFromStorage, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, []);

    if (!active) return null;

    // Sin sesión de agencia no hay conexiones de quién hablar: el estado en localStorage es de la
    // sesión anterior, y el banner se dibujaba en `/login` con un «Reconectar» a una pantalla que
    // pide login (card OGGNnRpB). El logout ya lo borra; las pantallas de entrada cubren la sesión
    // que venció sin logout, que conserva sus datos en localStorage.
    if (!LocalStorage.hasUserData() || LocalStorage.fetchUserType() !== 'AGENCY_USER') return null;
    if (AUTH_PATHS.includes(location.pathname)) return null;

    // Pago tiene prioridad — solo un banner a la vez (continuidad del servicio > sync).
    if (LocalStorage.fetchHasPaymentSuspensionWarning()) return null;

    // Usuario ya esta atendiendo el problema — no distraer.
    if (location.pathname === CONNECTIONS_PATH) return null;

    // Dismiss: si el fingerprint actual coincide con el dismissed, hide.
    // Si el set cambió (canal nuevo desconectado, o se reconectó uno y quedan
    // otros), el fingerprint cambia y el banner vuelve a aparecer.
    const currentFp = fingerprintOf(channels);
    if (dismissedFp && dismissedFp === currentFp) return null;

    const handleCtaClick = () => {
        navigate(CONNECTIONS_PATH);
    };

    const handleDismiss = () => {
        LocalStorage.updateDismissedDisconnectedFingerprint(currentFp);
        setDismissedFp(currentFp);
    };

    const displayedChannels = channels.slice(0, MAX_LOGOS_INLINE);
    const extraCount = channels.length - displayedChannels.length;

    let leadText;
    if (channels.length === 0) {
        // Fallback defensivo: backend sin canales o pre-deploy
        leadText = t('disconnected_credentials_banner.lead_generic');
    } else if (channels.length === 1) {
        // Un canal sin nombre acá cae al de `channels.js` antes que al código crudo.
        const channelName = CHANNEL_DISPLAY_NAMES[channels[0]] || CHANNEL_INFO_BY_CHANNEL[channels[0]]?.Spanish || channels[0];
        leadText = t('disconnected_credentials_banner.lead_one', { channel: channelName, interpolation: { escapeValue: false } });
    } else {
        leadText = t('disconnected_credentials_banner.lead_many', { count: channels.length });
    }

    return (
        <div className="disconnected-credentials-banner" role="status" aria-live="polite">
            <div className="disconnected-credentials-banner-inner">
                <span className="disconnected-credentials-banner-pulse" aria-hidden="true"/>

                {displayedChannels.length > 0 && (
                    <div className="disconnected-credentials-banner-logos" aria-hidden="true">
                        {displayedChannels.map((channel) => (
                            <span key={channel} className="disconnected-credentials-banner-logo-slot">
                                <ChannelLogo channel={channel} useOld={false} height={18} hspace={0} vspace={0}/>
                            </span>
                        ))}
                        {extraCount > 0 && (
                            <span className="disconnected-credentials-banner-extra">+{extraCount}</span>
                        )}
                    </div>
                )}

                <div className="disconnected-credentials-banner-copy">
                    <span className="disconnected-credentials-banner-lead">{leadText}</span>
                    <span className="disconnected-credentials-banner-body">
                        {t('disconnected_credentials_banner.body')}
                    </span>
                </div>

                <button
                    className="disconnected-credentials-banner-cta"
                    onClick={handleCtaClick}
                    aria-label={t('disconnected_credentials_banner.cta_aria')}
                >
                    {t('disconnected_credentials_banner.cta')}
                    <FontAwesomeIcon
                        icon={faArrowRight}
                        className="disconnected-credentials-banner-cta-arrow"
                        aria-hidden="true"
                    />
                </button>

                <button
                    type="button"
                    className="disconnected-credentials-banner-dismiss"
                    onClick={handleDismiss}
                    aria-label={t('disconnected_credentials_banner.dismiss_aria')}
                    title={t('disconnected_credentials_banner.dismiss_aria')}
                >
                    <FontAwesomeIcon icon={faXmark} aria-hidden="true"/>
                </button>
            </div>
        </div>
    );
};
